import type {
  AssistantDialogMessage,
  ThreadMessage,
} from "@/shared/components/SupportThread";
import type { SupportRequestQueryType } from "@/types/graphql/partner.generated";

type Request = NonNullable<SupportRequestQueryType["supportRequest"]>;
type MessageNode = Request["messages"][number];
type DialogNode = Request["assistantMessages"][number];

interface PersonLike {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

export function displayName(
  person: PersonLike | null | undefined
): string | null {
  if (!person) return null;
  const full = [person.firstName, person.lastName].filter(Boolean).join(" ");
  return person.displayName || full || person.email || null;
}

/** Сообщения ленты из сгенерированных типов в плоский формат общей ленты. */
export function toThreadMessages(nodes: MessageNode[]): ThreadMessage[] {
  return nodes.map((m) => ({
    id: m.id,
    author: `${m.author}`,
    channel: `${m.channel}`,
    body: m.body,
    internal: m.internal,
    viaAssistant: m.viaAssistant,
    event: m.event ? `${m.event}` : null,
    staffName: displayName(m.staff),
    eventUserName: displayName(m.eventUser),
    fromEmail: m.fromEmail ?? null,
    attachments: m.attachments.map((a) => ({ ...a })),
    skippedAttachments: m.skippedAttachments,
    hasOriginal: m.hasOriginal,
    createdAt: String(m.createdAt),
  }));
}

/** Реплики зрителя и ответы помощника; отказы, ошибки и служебное скрыты. */
export function toDialogMessages(
  nodes: DialogNode[]
): AssistantDialogMessage[] {
  return nodes
    .filter((m) => `${m.kind}` === "TEXT")
    .map((m) => ({
      id: m.id,
      role: `${m.role}`,
      text: m.text,
      createdAt: String(m.createdAt),
    }));
}

const SUMMARY_MAX_LENGTH = 160;

/** Кого подхватываем и что уже известно из деталей помощника (спека 3.1). */
export function takeoverHint(request: Pick<Request, "user" | "details">): {
  name: string;
  summary: string | null;
} {
  const name = displayName(request.user) ?? "";
  const details = request.details?.replace(/\s+/g, " ").trim();
  if (!details) return { name, summary: null };
  const sentence = /^(.+?[.!?])(\s|$)/.exec(details)?.[1] ?? details;
  return {
    name,
    summary:
      sentence.length > SUMMARY_MAX_LENGTH
        ? `${sentence.slice(0, SUMMARY_MAX_LENGTH - 3).trimEnd()}...`
        : sentence,
  };
}

/**
 * После ответа, решения и назначения: карточка, список, счётчик меню и
 * карточка дашборда (счётчик сам освежается только при смене адреса, а
 * карточка открывается query-параметром).
 */
export const REQUEST_REFETCH_QUERIES = [
  "SupportRequest",
  "SupportRequestsList",
  "SupportRequestsOpenCount",
  "SupportRequestsDashboard",
] as const;

/** Опрос открытой карточки: ответ зрителя письмом появляется без переоткрытия. */
export const REQUEST_CARD_POLL_MS = 30_000;

// Порядок важен: ASSIGNEE_NOT_FOUND раньше NOT_FOUND, совпадение по подстроке.
const ERROR_KEYS = {
  ACTIVE_REQUEST_EXISTS: "requests.errors.ACTIVE_REQUEST_EXISTS",
  STATUS_TRANSITION_NOT_ALLOWED:
    "requests.errors.STATUS_TRANSITION_NOT_ALLOWED",
  REQUEST_CHANGED: "requests.errors.REQUEST_CHANGED",
  EMPTY_MESSAGE: "requests.errors.EMPTY_MESSAGE",
  MESSAGE_TOO_LONG: "requests.errors.MESSAGE_TOO_LONG",
  ASSIGNEE_NOT_FOUND: "requests.errors.ASSIGNEE_NOT_FOUND",
  REQUEST_ALREADY_OPEN: "requests.errors.REQUEST_ALREADY_OPEN",
  ATTACHMENT_HOST_NOT_ALLOWED: "requests.errors.ATTACHMENT_HOST_NOT_ALLOWED",
  FORBIDDEN: "requests.errors.FORBIDDEN",
  NOT_FOUND: "requests.errors.NOT_FOUND",
} as const;

interface GraphQLErrorLike {
  message?: string;
  extensions?: { code?: unknown } | null;
}

/**
 * Код ошибки бэкенда (текст UserInputError/CodedError или extensions.code,
 * как у FORBIDDEN с текстом «Forbidden») в ключ перевода; сырой текст на
 * экран не идёт.
 */
export function requestErrorKey(error: {
  message?: string;
  graphQLErrors?: readonly (GraphQLErrorLike | null | undefined)[];
}): string {
  const codes = [
    ...(error.graphQLErrors ?? []).flatMap((e) => [
      e?.message ?? "",
      typeof e?.extensions?.code === "string" ? e.extensions.code : "",
    ]),
    error.message ?? "",
  ];
  for (const [code, key] of Object.entries(ERROR_KEYS)) {
    if (codes.some((m) => m.includes(code))) return key;
  }
  return "requests.errors.unknown";
}
